"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";

import { useUser } from "@/features/auth/hooks/useUser";
import { isAdminEmail } from "@/lib/auth/admin";
import { HEALTH_BUCKETS, type HealthBucketStyle } from "@/lib/workers/healthBuckets";
import {
  getWorkerHealthSummary,
  type WorkerHealthSummary,
} from "../../../../app/admin/workers/actions";

const REFRESH_MS = 60_000;

/** The visual fields of a shared bucket (its urgency rank stays in the table). */
function pick({ word, dot, chip, text }: HealthBucketStyle) {
  return { word, dot, chip, text };
}

/**
 * Health buckets, WORST FIRST. The order is the same urgency ordering
 * `analytics.worker_health` sorts by, so the header and the dashboard can never
 * disagree about what "the worst thing" is.
 *
 * `rail` is a solid 2px edge in the UNDILUTED bucket colour. It only appears
 * from ALERT upward: a 10%-alpha tint is a small colour shift that peripheral
 * vision misses, but a saturated vertical edge is a large, high-chroma change
 * that costs zero width. `ping` is spent exactly once, at the top rung — a
 * permanent animation in the global header is a cost on every route.
 *
 * LAGGING and NEVER_RAN deliberately get neither. A worker past 1.5x its
 * interval is the most common non-OK state on a fleet whose cadences run from
 * 10 minutes to daily, and it usually heals on the next tick. Painting the
 * header for it is the fastest way to teach an eye to stop reading this.
 */
// Words and colours come from the table the /admin/workers page uses too, so
// "2 LAGGING" here lands on two LAGGING labels there. Only the badge's own
// escalation (rail, ping) is defined locally.
const BUCKETS = [
  { key: "down",       ...pick(HEALTH_BUCKETS.DOWN),       rail: "bg-red-600",    ping: true  },
  { key: "failing",    ...pick(HEALTH_BUCKETS.FAILING),    rail: "bg-red-500",    ping: true  },
  { key: "stale_data", ...pick(HEALTH_BUCKETS.STALE_DATA), rail: "bg-purple-400", ping: false },
  { key: "degraded",   ...pick(HEALTH_BUCKETS.DEGRADED),   rail: "bg-[#e35e1c]",  ping: false },
  { key: "lagging",    ...pick(HEALTH_BUCKETS.LAGGING),    rail: null,            ping: false },
  { key: "never_ran",  ...pick(HEALTH_BUCKETS.NEVER_RAN),  rail: null,            ping: false },
] as const satisfies ReadonlyArray<{
  key: keyof WorkerHealthSummary;
  word: string;
  dot: string;
  chip: string;
  text: string;
  rail: string | null;
  ping: boolean;
}>;

const OK_TONE = {
  ...pick(HEALTH_BUCKETS.OK),
  rail: null,
  ping: false,
} as const;

/**
 * The summary RPC catches its own errors and returns all-zeros, so `total === 0`
 * means "the reading failed", not "there are no workers". Today that renders
 * `null` — visually identical to "you are not an admin" and to "still loading".
 *
 * That is the same fail-open shape as the DB bug fixed in migration
 * 20260910000000, where an emptied table made the freshness probe NULL and
 * switched off the alarm that watched it. A health indicator that disappears
 * when its own sensor dies is that bug at the UI layer, so it says so instead.
 */
const NO_READING_TONE = {
  word: "NO READING",
  dot: "bg-[#6a7785]",
  chip: "bg-[#6a7785]/10",
  text: "text-[#8f98a0]",
  rail: null,
  ping: false,
} as const;

type Tone = {
  word: string;
  dot: string;
  chip: string;
  text: string;
  rail: string | null;
  ping: boolean;
};

/**
 * The badge names the SINGLE worst bucket and its count — never a mixed tally.
 * `2 DOWN` is a symptom you can act on; `11/13` is a ratio you have to decode
 * into one first, and the denominator means nothing unless you already know the
 * fleet size.
 */
function read(s: WorkerHealthSummary): {
  tone: Tone;
  count: number | null;
  breakdown: { word: string; count: number; text: string; dot: string }[];
  okCount: number;
} {
  if (s.total === 0) {
    return { tone: NO_READING_TONE, count: null, breakdown: [], okCount: 0 };
  }

  const breakdown: ReturnType<typeof read>["breakdown"] = [];
  let tone: Tone = OK_TONE;
  let count: number | null = null;

  for (const b of BUCKETS) {
    const n = s[b.key];
    if (n <= 0) continue;
    if (count === null) {
      tone = b;
      count = n;
    }
    breakdown.push({ word: b.word, count: n, text: b.text, dot: b.dot });
  }

  // Derive OK from the remainder rather than trusting `s.ok`, so the numbers
  // still add up if the RPC ever grows a bucket this component doesn't know.
  const unhealthy = breakdown.reduce((a, b) => a + b.count, 0);
  const okCount = Math.max(0, s.total - unhealthy);

  return { tone, count: count ?? okCount, breakdown, okCount };
}

export function WorkerHealthBadge() {
  const { user, loading } = useUser();
  const isAdmin = isAdminEmail(user?.email);
  const pathname = usePathname();
  const [summary, setSummary] = useState<WorkerHealthSummary | null>(null);

  // The badge sits in the GLOBAL header, so it used to poll from every admin
  // tab on every route, forever — a server action per minute per tab, each one
  // a blocking cache regeneration (see the cache file). Now it fetches once on
  // mount everywhere, so the OK/total chip still works site-wide, and only
  // keeps a live interval running while an /admin page is actually open.
  const onAdminPage = pathname.startsWith("/admin");

  useEffect(() => {
    if (!isAdmin) return;

    let cancelled = false;
    const tick = async () => {
      const s = await getWorkerHealthSummary();
      // null = throttled (or the server disagrees about admin): keep the
      // last reading rather than flashing back to the placeholder.
      if (!cancelled && s) setSummary(s);
    };

    tick();
    if (!onAdminPage) {
      return () => {
        cancelled = true;
      };
    }

    const t = setInterval(tick, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [isAdmin, onAdminPage]);

  if (loading || !isAdmin) return null;

  // Reserve the footprint before the first reading lands, so the search input
  // beside it doesn't get shoved sideways on every page load.
  if (!summary) {
    return (
      <div
        className="hidden h-6 min-w-[86px] rounded-sm bg-[#2a475e]/25 lg:block"
        aria-hidden
      />
    );
  }

  const { tone, count, breakdown, okCount } = read(summary);
  const isCritical = tone.ping;
  const workerWord = summary.total === 1 ? "worker" : "workers";

  const label =
    tone === NO_READING_TONE
      ? tone.word
      : `${count ?? okCount} ${tone.word}`;

  const sentence =
    tone === NO_READING_TONE
      ? "Worker health could not be read."
      : tone === OK_TONE
        ? `All ${summary.total} ${workerWord} healthy.`
        : `${count} ${tone.word.toLowerCase()} of ${summary.total} ${workerWord}.`;

  return (
    // Critical states escape the desktop-only rule: a dead worker should still
    // reach an admin who is on a phone. Everything quieter stays hidden there,
    // where 90px of header is expensive.
    <div className={`group relative ${isCritical ? "block" : "hidden lg:block"}`}>
      <Link
        href="/admin/workers"
        aria-label={`${sentence} Open the workers dashboard.`}
        className="flex min-w-[86px] items-center rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#66c0f4]"
      >
        {/* The site's own status-chip primitive (PulseMasthead's "Live"
            beacon): tint container, no border. The border is what separates a
            control from an indicator here — this is not a button. */}
        <span
          className={`relative flex items-center gap-1.5 overflow-hidden rounded-sm py-0.5 pr-1.5 transition-colors ${
            tone.chip
          } ${tone.rail ? "pl-2.5" : "pl-1.5"} group-hover:brightness-125`}
        >
          {tone.rail && (
            <span
              className={`absolute inset-y-0 left-0 w-[2px] ${tone.rail}`}
              aria-hidden
            />
          )}

          <span className="relative flex h-1.5 w-1.5 shrink-0">
            {tone.ping && (
              <span
                className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 motion-reduce:hidden ${tone.dot}`}
              />
            )}
            <span
              className={`relative inline-flex h-1.5 w-1.5 rounded-full ${tone.dot}`}
            />
          </span>

          <span
            className={`whitespace-nowrap text-[10px] font-bold uppercase tracking-[0.18em] ${tone.text}`}
          >
            {label}
          </span>
        </span>
      </Link>

      {/* Hover/focus card. It is a descendant of the .group wrapper — a sibling
          would never receive group-hover. */}
      <div className="pointer-events-none absolute right-0 top-full z-50 mt-1.5 w-max min-w-[180px] origin-top-right scale-95 rounded-lg border border-[#2a475e] bg-[#16202d] p-2.5 opacity-0 shadow-xl transition-all duration-100 group-hover:pointer-events-auto group-hover:scale-100 group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:scale-100 group-focus-within:opacity-100">
        <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-[#6a7785]">
          Worker health
        </p>

        {tone === NO_READING_TONE ? (
          <p className="max-w-[200px] text-xs leading-relaxed text-[#c7d5e0]">
            The health summary came back empty. The workers may be fine — the
            check itself isn&apos;t reporting.
          </p>
        ) : (
          <ul className="space-y-1">
            {breakdown.map((b) => (
              <li
                key={b.word}
                className="flex items-center justify-between gap-5 text-xs"
              >
                <span className="flex items-center gap-1.5">
                  <span className={`h-1.5 w-1.5 rounded-full ${b.dot}`} />
                  <span className={b.text}>{b.word}</span>
                </span>
                <span className="font-[family-name:var(--font-geist-mono)] tabular-nums text-[#c7d5e0]">
                  {b.count}
                </span>
              </li>
            ))}
            <li
              className={`flex items-center justify-between gap-5 text-xs ${
                breakdown.length ? "border-t border-[#2a475e]/60 pt-1" : ""
              }`}
            >
              <span className="flex items-center gap-1.5">
                <span className={`h-1.5 w-1.5 rounded-full ${OK_TONE.dot}`} />
                <span className={OK_TONE.text}>OK</span>
              </span>
              <span className="font-[family-name:var(--font-geist-mono)] tabular-nums text-[#c7d5e0]">
                {okCount}
              </span>
            </li>
          </ul>
        )}

        <p className="mt-2 border-t border-[#2a475e]/60 pt-1.5 text-[10px] text-[#6a7785]">
          {/* With no reading there is no fleet size to report — "0 workers
              tracked" would be a number we do not actually have. */}
          {tone === NO_READING_TONE
            ? "Open dashboard →"
            : `${summary.total} ${workerWord} tracked · open dashboard →`}
        </p>
      </div>
    </div>
  );
}
